import { Header, Sidebar } from '@/components/layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Check,
  Database,
  Download,
  Edit,
  Eye,
  FileText,
  FlaskConical,
  Loader2,
  Mic,
  Play,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface TestCase {
  name: string;
  title: string;
  description: string;
  input_mode: string;
  audio_id?: string;
}

interface TestCases {
  transcription: TestCase[];
  summary: TestCase[];
}

interface Recording {
  audio_id: string;
  duration: number;
  finalized: boolean;
  created_at: string;
  user_id: number;
}

interface RecordingDetails {
  audio_id: string;
  duration: number;
  status: string;
  created_at: string;
  mode: string;
  segments: Array<{
    speaker: string;
    start: number;
    end: number;
    text: string;
  }>;
  segment_count: number;
  smart_segments: Array<{
    speaker: string;
    start: number;
    end: number;
    text: string;
    turn_order: number;
  }>;
  smart_segment_count: number;
  audio_url: string | null;
}

interface TestResult {
  case_name: string;
  test_type?: string;
  passed: boolean;
  score: number;
  summary: string;
  differences: Array<Record<string, unknown>>;
  expected: string;
  actual: string;
  timestamp?: string;
}

export default function TestSuite() {
  const { toast } = useToast();

  // Data states
  const [testCases, setTestCases] = useState<TestCases>({ transcription: [], summary: [] });
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [selectedCase, setSelectedCase] = useState<{ type: string; name: string } | null>(null);
  const [caseContent, setCaseContent] = useState<string>('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [selectedRecording, setSelectedRecording] = useState<RecordingDetails | null>(null);
  const [selectedRecordingUserId, setSelectedRecordingUserId] = useState<number | null>(null);
  const [exportName, setExportName] = useState<string>('');
  const [exportDescription, setExportDescription] = useState<string>('');
  const [exportType, setExportType] = useState<'transcription' | 'summary'>('transcription');

  // Loading states
  const [isLoadingCases, setIsLoadingCases] = useState(true);
  const [isLoadingRecordings, setIsLoadingRecordings] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch test cases on mount
  useEffect(() => {
    fetchTestCases();
  }, []);

  const fetchTestCases = async () => {
    setIsLoadingCases(true);
    try {
      const response = await fetch('/api/test-suite/cases', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTestCases(data.cases || { transcription: [], summary: [] });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load test cases',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to fetch test cases:', error);
    } finally {
      setIsLoadingCases(false);
    }
  };

  const fetchRecordings = async () => {
    setIsLoadingRecordings(true);
    try {
      const response = await fetch('/api/test-suite/recordings?limit=50', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRecordings(data.recordings || []);
      }
    } catch (error) {
      console.error('Failed to fetch recordings:', error);
    } finally {
      setIsLoadingRecordings(false);
    }
  };

  const loadTestCase = async (testType: string, caseName: string) => {
    try {
      const response = await fetch(`/api/test-suite/cases/${testType}/${caseName}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedCase({ type: testType, name: caseName });
        setCaseContent(data.content);
      }
    } catch (error) {
      console.error('Failed to load test case:', error);
    }
  };

  const saveTestCase = async () => {
    if (!selectedCase) return;

    setIsSaving(true);
    try {
      const response = await fetch(`/api/test-suite/cases/${selectedCase.type}/${selectedCase.name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: caseContent }),
      });

      if (response.ok) {
        toast({
          title: 'Saved',
          description: 'Test case saved successfully',
        });
        fetchTestCases();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to save test case',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to save test case:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const runTest = async () => {
    if (!selectedCase) return;

    setIsRunning(true);
    setTestResult(null);
    try {
      const response = await fetch('/api/test-suite/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          test_type: selectedCase.type,
          case_name: selectedCase.name,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const resultWithTimestamp = { ...data, timestamp: new Date().toISOString() };
        setTestResult(resultWithTimestamp);
        setTestResults((prev) => [resultWithTimestamp, ...prev.slice(0, 49)]); // Keep last 50 results
        toast({
          title: data.passed ? 'Test Passed' : 'Test Failed',
          description: data.summary,
          variant: data.passed ? 'default' : 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to run test',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to run test:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const runAllTests = async (testType: string) => {
    setIsRunning(true);
    try {
      const response = await fetch('/api/test-suite/run-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ test_type: testType, runs: 1 }),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Tests Complete',
          description: `${data.summary.passed}/${data.summary.total} passed (${Math.round(data.summary.consistency * 100)}%)`,
          variant: data.summary.passed === data.summary.total ? 'default' : 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to run all tests:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const deleteTestCase = async (testType: string, caseName: string) => {
    if (!confirm(`Delete test case "${caseName}"?`)) return;

    try {
      const response = await fetch(`/api/test-suite/cases/${testType}/${caseName}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Deleted',
          description: `Test case "${caseName}" deleted`,
        });
        fetchTestCases();
        if (selectedCase?.name === caseName) {
          setSelectedCase(null);
          setCaseContent('');
          setTestResult(null);
        }
      } else {
        toast({
          title: 'Error',
          description: 'Failed to delete test case',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to delete test case:', error);
    }
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return '?';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchRecordingDetails = async (audioId: string, userId?: number) => {
    setIsLoadingDetails(true);
    setSelectedRecording(null);
    setSelectedRecordingUserId(userId || null);
    try {
      const url = userId
        ? `/api/test-suite/recordings/${audioId}?owner_user_id=${userId}`
        : `/api/test-suite/recordings/${audioId}`;
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedRecording(data);
        setExportName(audioId.slice(0, 8));
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load recording details',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to fetch recording details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const exportTestCase = async () => {
    if (!selectedRecording || !exportName) return;

    setIsExporting(true);
    try {
      const response = await fetch('/api/test-suite/export-test-case', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          audio_id: selectedRecording.audio_id,
          test_type: exportType,
          case_name: exportName,
          description: exportDescription,
          owner_user_id: selectedRecordingUserId,
        }),
      });

      if (response.ok) {
        toast({
          title: 'Exported',
          description: `Test case saved as ${exportType}/${exportName}.yaml`,
        });
        fetchTestCases();
        setSelectedRecording(null);
        setExportName('');
        setExportDescription('');
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to export test case',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to export test case:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 dark:bg-neutral-900">
      <Header title="Prófunarsetur" />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-7xl">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FlaskConical className="h-8 w-8 text-primary" />
                <div>
                  <h1 className="font-bold text-2xl">Test Suite</h1>
                  <p className="text-neutral-500">Iterate on transcription and summary generation</p>
                </div>
              </div>
              <Button variant="outline" onClick={fetchTestCases}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>

            <Tabs defaultValue="cases" className="space-y-4">
              <TabsList>
                <TabsTrigger value="cases">Test Cases</TabsTrigger>
                <TabsTrigger value="recordings" onClick={fetchRecordings}>
                  Recordings
                </TabsTrigger>
                <TabsTrigger value="results">Results</TabsTrigger>
              </TabsList>

              {/* Test Cases Tab */}
              <TabsContent value="cases">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  {/* Test Case List */}
                  <Card className="lg:col-span-1">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Test Cases
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {isLoadingCases ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : (
                        <>
                          {/* Transcription Tests */}
                          <div>
                            <div className="mb-2 flex items-center justify-between">
                              <h3 className="flex items-center gap-2 font-medium">
                                <Mic className="h-4 w-4" />
                                Transcription
                              </h3>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => runAllTests('transcription')}
                                disabled={isRunning}
                              >
                                <Play className="mr-1 h-3 w-3" />
                                Run All
                              </Button>
                            </div>
                            <div className="space-y-1">
                              {testCases.transcription.map((tc) => (
                                <div
                                  key={tc.name}
                                  // biome-ignore lint/a11y/useSemanticElements: list item with click handler
                                  role="button"
                                  tabIndex={0}
                                  className={`group cursor-pointer rounded p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 ${
                                    selectedCase?.name === tc.name && selectedCase?.type === 'transcription'
                                      ? 'bg-neutral-100 dark:bg-neutral-800'
                                      : ''
                                  }`}
                                  onClick={() => loadTestCase('transcription', tc.name)}
                                  onKeyDown={(e) => e.key === 'Enter' && loadTestCase('transcription', tc.name)}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{tc.title || tc.name}</span>
                                    <div className="flex items-center gap-1">
                                      <Badge variant={tc.input_mode === 'database' ? 'secondary' : 'outline'}>
                                        {tc.input_mode}
                                      </Badge>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteTestCase('transcription', tc.name);
                                        }}
                                      >
                                        <Trash2 className="h-3 w-3 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                  {tc.description && (
                                    <p className="truncate text-neutral-500 text-xs">{tc.description}</p>
                                  )}
                                </div>
                              ))}
                              {testCases.transcription.length === 0 && (
                                <p className="py-2 text-neutral-500 text-sm">No test cases</p>
                              )}
                            </div>
                          </div>

                          {/* Summary Tests */}
                          <div>
                            <div className="mb-2 flex items-center justify-between">
                              <h3 className="flex items-center gap-2 font-medium">
                                <FileText className="h-4 w-4" />
                                Summary
                              </h3>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => runAllTests('summary')}
                                disabled={isRunning}
                              >
                                <Play className="mr-1 h-3 w-3" />
                                Run All
                              </Button>
                            </div>
                            <div className="space-y-1">
                              {testCases.summary.map((tc) => (
                                <div
                                  key={tc.name}
                                  // biome-ignore lint/a11y/useSemanticElements: list item with click handler
                                  role="button"
                                  tabIndex={0}
                                  className={`group cursor-pointer rounded p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 ${
                                    selectedCase?.name === tc.name && selectedCase?.type === 'summary'
                                      ? 'bg-neutral-100 dark:bg-neutral-800'
                                      : ''
                                  }`}
                                  onClick={() => loadTestCase('summary', tc.name)}
                                  onKeyDown={(e) => e.key === 'Enter' && loadTestCase('summary', tc.name)}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{tc.title || tc.name}</span>
                                    <div className="flex items-center gap-1">
                                      <Badge variant={tc.input_mode === 'database' ? 'secondary' : 'outline'}>
                                        {tc.input_mode}
                                      </Badge>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteTestCase('summary', tc.name);
                                        }}
                                      >
                                        <Trash2 className="h-3 w-3 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                  {tc.description && (
                                    <p className="truncate text-neutral-500 text-xs">{tc.description}</p>
                                  )}
                                </div>
                              ))}
                              {testCases.summary.length === 0 && (
                                <p className="py-2 text-neutral-500 text-sm">No test cases</p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Test Case Editor */}
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>
                            {selectedCase ? `${selectedCase.type}/${selectedCase.name}` : 'Select a test case'}
                          </CardTitle>
                          <CardDescription>Edit test case YAML and run tests</CardDescription>
                        </div>
                        {selectedCase && (
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={saveTestCase} disabled={isSaving}>
                              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit className="h-4 w-4" />}
                              <span className="ml-2">Save</span>
                            </Button>
                            <Button onClick={runTest} disabled={isRunning || !selectedCase}>
                              {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                              <span className="ml-2">Run Test</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {selectedCase ? (
                        <div className="space-y-4">
                          <Textarea
                            value={caseContent}
                            onChange={(e) => setCaseContent(e.target.value)}
                            className="min-h-[400px] font-mono text-sm"
                            placeholder="Test case YAML content..."
                          />

                          {/* Test Result */}
                          {testResult && (
                            <Card className={testResult.passed ? 'border-green-500' : 'border-red-500'}>
                              <CardHeader className="pb-2">
                                <div className="flex items-center gap-2">
                                  {testResult.passed ? (
                                    <Check className="h-5 w-5 text-green-500" />
                                  ) : (
                                    <X className="h-5 w-5 text-red-500" />
                                  )}
                                  <CardTitle className="text-lg">
                                    {testResult.passed ? 'Test Passed' : 'Test Failed'}
                                  </CardTitle>
                                  <Badge variant={testResult.passed ? 'default' : 'destructive'}>
                                    {Math.round(testResult.score * 100)}%
                                  </Badge>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <p className="text-sm">{testResult.summary}</p>

                                {testResult.differences.length > 0 && (
                                  <div>
                                    <h4 className="mb-2 font-medium text-sm">Differences:</h4>
                                    <div className="space-y-1 rounded bg-neutral-100 p-3 font-mono text-sm dark:bg-neutral-800">
                                      {testResult.differences.slice(0, 5).map((diff, i) => (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: differences don't have unique keys
                                        <div key={i} className="text-red-600 dark:text-red-400">
                                          {JSON.stringify(diff)}
                                        </div>
                                      ))}
                                      {testResult.differences.length > 5 && (
                                        <div className="text-neutral-500">
                                          ... and {testResult.differences.length - 5} more
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-neutral-500">
                          <FileText className="mb-4 h-12 w-12" />
                          <p>Select a test case from the list to view and edit</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Recordings Tab */}
              <TabsContent value="recordings">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Recordings List */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Database className="h-5 w-5" />
                            Available Recordings
                          </CardTitle>
                          <CardDescription>Click a recording to view transcript and export</CardDescription>
                        </div>
                        <Button variant="outline" onClick={fetchRecordings} disabled={isLoadingRecordings}>
                          {isLoadingRecordings ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          <span className="ml-2">Refresh</span>
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingRecordings ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : recordings.length === 0 ? (
                        <p className="py-8 text-center text-neutral-500">No recordings found. Click refresh to load.</p>
                      ) : (
                        <ScrollArea className="h-[500px]">
                          <div className="space-y-2">
                            {recordings.map((rec) => (
                              <div
                                key={rec.audio_id}
                                // biome-ignore lint/a11y/useSemanticElements: list item with click handler
                                role="button"
                                tabIndex={0}
                                className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                                  selectedRecording?.audio_id === rec.audio_id
                                    ? 'border-primary bg-primary/5'
                                    : 'hover:bg-neutral-50 dark:hover:bg-neutral-800'
                                }`}
                                onClick={() => fetchRecordingDetails(rec.audio_id, rec.user_id)}
                                onKeyDown={(e) => e.key === 'Enter' && fetchRecordingDetails(rec.audio_id, rec.user_id)}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-mono text-xs">{rec.audio_id.slice(0, 20)}...</span>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={rec.finalized ? 'default' : 'secondary'}>
                                      {rec.finalized ? 'finalized' : 'active'}
                                    </Badge>
                                    <Eye className="h-4 w-4 text-neutral-400" />
                                  </div>
                                </div>
                                <div className="mt-1 flex items-center gap-4 text-neutral-500 text-xs">
                                  <span>{formatDuration(rec.duration)}</span>
                                  <span>{new Date(rec.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recording Details & Export */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {selectedRecording ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedRecording(null)}
                              className="mr-2"
                            >
                              <ArrowLeft className="h-4 w-4" />
                            </Button>
                            Transcript
                          </>
                        ) : (
                          <>
                            <FileText className="h-5 w-5" />
                            Recording Details
                          </>
                        )}
                      </CardTitle>
                      {selectedRecording && (
                        <CardDescription>
                          {selectedRecording.segment_count} raw • {selectedRecording.smart_segment_count || 0} smart
                          reviewed • {selectedRecording.mode} mode
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      {isLoadingDetails ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : selectedRecording ? (
                        <div className="space-y-4">
                          {/* Audio Player */}
                          {selectedRecording.audio_url ? (
                            <div className="rounded-lg border bg-neutral-50 p-3 dark:bg-neutral-900">
                              <h4 className="mb-2 flex items-center gap-1 font-medium text-neutral-500 text-xs">
                                <Mic className="h-3 w-3" />
                                Audio Recording
                              </h4>
                              {/* biome-ignore lint/a11y/useMediaCaption: test recordings don't have captions */}
                              <audio controls className="h-8 w-full" src={selectedRecording.audio_url}>
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                          ) : (
                            <div className="rounded-lg border bg-neutral-100 p-3 text-center dark:bg-neutral-800">
                              <p className="text-neutral-500 text-xs">No audio file available</p>
                            </div>
                          )}

                          {/* Raw vs Smart Segments Comparison */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* Raw Segments */}
                            <div>
                              <h4 className="mb-2 flex items-center gap-1 font-medium text-neutral-500 text-xs">
                                <Mic className="h-3 w-3" />
                                Raw ASR ({selectedRecording.segment_count})
                              </h4>
                              <ScrollArea className="h-[240px] rounded-lg border bg-neutral-50 p-2 dark:bg-neutral-900">
                                {selectedRecording.segments.length === 0 ? (
                                  <p className="py-4 text-center text-neutral-500 text-xs">No segments</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {selectedRecording.segments.map((seg, i) => (
                                      <div
                                        // biome-ignore lint/suspicious/noArrayIndexKey: segments may not have unique keys
                                        key={i}
                                        className="rounded border bg-white p-1.5 text-xs dark:bg-neutral-800"
                                      >
                                        <div className="mb-0.5 flex items-center gap-1">
                                          <Badge variant="outline" className="px-1 py-0 text-[10px]">
                                            {seg.speaker}
                                          </Badge>
                                          {(seg.start > 0 || seg.end > 0) && (
                                            <span className="text-[10px] text-neutral-400">
                                              {seg.start.toFixed(1)}s - {seg.end.toFixed(1)}s
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-neutral-700 dark:text-neutral-300">{seg.text}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </ScrollArea>
                            </div>
                            {/* Smart Segments */}
                            <div>
                              <h4 className="mb-2 flex items-center gap-1 font-medium text-neutral-500 text-xs">
                                <Check className="h-3 w-3" />
                                Smart Review ({selectedRecording.smart_segment_count || 0})
                              </h4>
                              <ScrollArea className="h-[240px] rounded-lg border bg-green-50 p-2 dark:bg-green-950">
                                {!selectedRecording.smart_segments || selectedRecording.smart_segments.length === 0 ? (
                                  <p className="py-4 text-center text-neutral-500 text-xs">Not reviewed yet</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {selectedRecording.smart_segments.map((seg, i) => (
                                      <div
                                        // biome-ignore lint/suspicious/noArrayIndexKey: segments may not have unique keys
                                        key={i}
                                        className="rounded border border-green-200 bg-white p-1.5 text-xs dark:border-green-800 dark:bg-neutral-800"
                                      >
                                        <div className="mb-0.5 flex items-center gap-1">
                                          <Badge variant="default" className="bg-green-600 px-1 py-0 text-[10px]">
                                            {seg.speaker}
                                          </Badge>
                                          <span className="text-[10px] text-neutral-400">#{seg.turn_order}</span>
                                        </div>
                                        <span className="text-neutral-700 dark:text-neutral-300">{seg.text}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </ScrollArea>
                            </div>
                          </div>

                          {/* Export Form */}
                          <div className="space-y-3 border-t pt-4">
                            <h4 className="flex items-center gap-2 font-medium">
                              <Download className="h-4 w-4" />
                              Export as Test Case
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor="exportName">Test Name</Label>
                                <Input
                                  id="exportName"
                                  value={exportName}
                                  onChange={(e) => setExportName(e.target.value)}
                                  placeholder="my-test-case"
                                />
                              </div>
                              <div>
                                <Label htmlFor="exportType">Test Type</Label>
                                <select
                                  id="exportType"
                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  value={exportType}
                                  onChange={(e) => setExportType(e.target.value as 'transcription' | 'summary')}
                                >
                                  <option value="transcription">Transcription</option>
                                  <option value="summary">Summary</option>
                                </select>
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="exportDesc">Description (optional)</Label>
                              <Input
                                id="exportDesc"
                                value={exportDescription}
                                onChange={(e) => setExportDescription(e.target.value)}
                                placeholder="Test case description..."
                              />
                            </div>
                            <Button onClick={exportTestCase} disabled={isExporting || !exportName} className="w-full">
                              {isExporting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="mr-2 h-4 w-4" />
                              )}
                              Export Test Case
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-neutral-500">
                          <Database className="mb-4 h-12 w-12" />
                          <p>Select a recording to view transcript</p>
                          <p className="text-sm">and export as test case</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Results Tab */}
              <TabsContent value="results">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Test Results History
                    </CardTitle>
                    <CardDescription>
                      {testResults.length > 0
                        ? `${testResults.length} test run${testResults.length === 1 ? '' : 's'} recorded this session`
                        : 'Run tests to see results here'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {testResults.length === 0 ? (
                      <div className="py-8 text-center">
                        <Play className="mx-auto mb-4 h-12 w-12 text-neutral-300" />
                        <p className="text-neutral-500">No test results yet</p>
                        <p className="text-neutral-400 text-sm">
                          Run a test from the Test Cases tab to see results here
                        </p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[500px]">
                        <div className="space-y-3">
                          {testResults.map((result, index) => (
                            <div
                              // biome-ignore lint/suspicious/noArrayIndexKey: results don't have unique keys
                              key={index}
                              className={`rounded-lg border p-4 ${
                                result.passed
                                  ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950'
                                  : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950'
                              }`}
                            >
                              <div className="mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {result.passed ? (
                                    <Check className="h-5 w-5 text-green-600" />
                                  ) : (
                                    <X className="h-5 w-5 text-red-600" />
                                  )}
                                  <span className="font-medium">{result.case_name}</span>
                                  <Badge variant="outline">{result.test_type}</Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant={result.passed ? 'default' : 'destructive'}>
                                    {Math.round(result.score * 100)}%
                                  </Badge>
                                  <span className="text-neutral-500 text-xs">
                                    {result.timestamp ? new Date(result.timestamp).toLocaleTimeString() : ''}
                                  </span>
                                </div>
                              </div>
                              <p className="text-neutral-600 text-sm dark:text-neutral-400">{result.summary}</p>
                              {result.differences && result.differences.length > 0 && (
                                <details className="mt-2">
                                  <summary className="cursor-pointer text-neutral-500 text-sm hover:text-neutral-700">
                                    View {result.differences.length} difference
                                    {result.differences.length === 1 ? '' : 's'}
                                  </summary>
                                  <pre className="mt-2 overflow-x-auto rounded bg-neutral-100 p-2 text-xs dark:bg-neutral-800">
                                    {JSON.stringify(result.differences, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </div>
  );
}
